import React, {
    createContext,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import { SOURCE_LANGUAGE } from '../constants/languages';
import { useSupportedLanguages } from '../hooks/useSupportedLanguages';
import { applyDocumentLanguage } from '../utils/documentLanguage';
import { findLanguage } from '../utils/languageList';
import { getStoredDirection, getStoredLanguage, storeLanguage } from '../utils/languageStorage';
import '../styles/rtl.css';

export const LanguageContext = createContext(null);

const IDLE_STATUS = Object.freeze({ state: 'idle', message: '' });

/**
 * Holds the chosen language for the whole app.
 *
 * The languages on offer come from the backend (GET /translation/v1/languages) with English
 * always added first; see hooks/useSupportedLanguages.
 *
 * Choosing a language saves it and reloads the page. That is deliberate: English is simply the
 * page as React drew it, so reloading is the safest way back to it, and every other language
 * is produced fresh by the page translator (which also reuses the saved translation cache, so
 * the reload costs little).
 */
export const LanguageProvider = ({ children }) => {
    const { languages, status: languagesStatus, reload: reloadLanguages } = useSupportedLanguages();
    const [language] = useState(getStoredLanguage);
    const [translationStatus, setStatus] = useState(IDLE_STATUS);

    // The saved language's details. If it is not in the list yet, fall back to the direction we
    // saved with it, so the page direction is still right.
    const languageInfo = useMemo(
        () =>
            findLanguage(languages, language) || {
                code: language,
                label: language,
                englishName: language,
                dir: getStoredDirection(),
            },
        [languages, language]
    );

    // Before the browser paints, so Arabic never flashes in left-to-right.
    useLayoutEffect(() => {
        applyDocumentLanguage(languageInfo);
    }, [languageInfo]);

    // The backend no longer offers the saved language: go back to English (once).
    useEffect(() => {
        if (
            languagesStatus === 'loaded' &&
            language !== SOURCE_LANGUAGE &&
            !findLanguage(languages, language)
        ) {
            storeLanguage(SOURCE_LANGUAGE, 'ltr');
            window.location.reload();
        }
    }, [languagesStatus, languages, language]);

    const changeLanguage = useCallback(
        (code) => {
            const target = findLanguage(languages, code);
            if (!target || code === language) return;
            storeLanguage(target.code, target.dir);
            window.location.reload();
        },
        [languages, language]
    );

    // Only re-render consumers when the status really changes.
    const setTranslationStatus = useCallback((next) => {
        setStatus((previous) =>
            previous.state === next.state && previous.message === next.message ? previous : next
        );
    }, []);

    const value = useMemo(
        () => ({
            language,
            languageInfo,
            isRtl: languageInfo.dir === 'rtl',
            supportedLanguages: languages,
            languagesStatus,
            reloadLanguages,
            changeLanguage,
            translationStatus,
            setTranslationStatus,
        }),
        [language, languageInfo, languages, languagesStatus, reloadLanguages, changeLanguage, translationStatus, setTranslationStatus]
    );

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
